import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cx } from "./ui.js";
import { desempilhar, ehDoTopo, empilhar, rolagemPresa, topo, PILHA_VAZIA } from "./camadas.js";

/**
 * As camadas abertas agora, da mais antiga à mais nova. É do módulo, e não de cada `Sobreposicao`,
 * porque a pergunta "sou eu quem está em cima?" só tem resposta olhando as outras. A conta está em
 * `camadas.ts`; aqui só fica o que toca o DOM.
 */
let pilha = PILHA_VAZIA;
let proximoId = 1;
/** O elemento de cada camada, para tornar inerte a que ficou por baixo. */
const raizes = new Map<number, HTMLElement>();
/** O `overflow` do `body` antes da primeira camada, que é o que se devolve quando a última fecha. */
let rolagemOriginal = "";

/**
 * Conteúdo sobre a tela inteira.
 *
 * A primeira do sistema, e por isso escrita com cuidado: em todo o resto o
 * padrão é painel inline, que expande dentro da lista. Aqui não serve — ver uma
 * foto melhor significa dar a tela a ela.
 *
 * Vai por portal no `body` para não depender de nenhum ancestral: basta um
 * `transform` no caminho para um `fixed` passar a se posicionar em relação a ele
 * e a sobreposição aparecer no lugar errado.
 *
 * `tamanho="tela"` é para o que precisa da tela inteira, sem moldura nem rolagem própria — o visor de
 * imagens, que dá zoom e arrasta dentro dela. O conteúdo recebe uma coluna de altura cheia, e quem
 * a usa decide como dividi-la. O padrão, `painel`, é o de sempre.
 *
 * **Uma sobre a outra.** O visor abre sobre a tela de escolha da foto, e as duas são `Sobreposicao`.
 * Só a camada de cima responde ao Esc, ao Tab (a armadilha de foco) e ao clique no fundo; a de baixo
 * fica inerte — fora do Tab, do toque e do leitor de tela — até a de cima fechar. A página só volta a
 * rolar quando a última fecha, e cada camada devolve o foco a quem a abriu.
 */
export function Sobreposicao({
  rotulo,
  aoFechar,
  tamanho = "painel",
  children,
}: {
  /** Como leitores de tela anunciam o que abriu. */
  rotulo: string;
  aoFechar: () => void;
  tamanho?: "painel" | "tela";
  children: React.ReactNode;
}) {
  const tela = tamanho === "tela";
  const raiz = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const idDaCamada = useRef<number | null>(null);

  // O `aoFechar` passa por uma ref, e o efeito abaixo não depende dele: se dependesse, quem passa uma
  // função nova a cada renderização faria a camada sair da pilha e entrar de novo — voltando ao topo,
  // por cima de outra que abriu depois — e puxaria o foco do campo em que se digita.
  const ultimoAoFechar = useRef(aoFechar);
  ultimoAoFechar.current = aoFechar;

  useEffect(() => {
    const id = proximoId++;
    idDaCamada.current = id;
    const focoAnterior = document.activeElement as HTMLElement | null;

    const abaixo = topo(pilha);
    pilha = empilhar(pilha, id);
    raizes.set(id, raiz.current!);
    // A página atrás não deve rolar junto — e a camada de baixo não deve receber toque nem foco.
    if (abaixo === undefined) {
      rolagemOriginal = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else {
      raizes.get(abaixo)!.inert = true;
    }
    painel.current?.focus();

    const aoTeclar = (e: KeyboardEvent) => {
      if (!ehDoTopo(pilha, id)) return;
      if (e.key === "Escape") ultimoAoFechar.current();
      // Na tela inteira não há "fora" para o Tab escapar: sem isto ele seguia para a página
      // escondida atrás, e o foco sumia de vista.
      if (tela && e.key === "Tab") prenderFoco(e, painel.current);
    };
    document.addEventListener("keydown", aoTeclar);

    return () => {
      document.removeEventListener("keydown", aoTeclar);
      const antes = topo(pilha);
      pilha = desempilhar(pilha, id);
      raizes.delete(id);
      idDaCamada.current = null;

      const agora = topo(pilha);
      if (!rolagemPresa(pilha)) document.body.style.overflow = rolagemOriginal;
      // A que ficou por baixo volta a responder — e antes de receber o foco de volta: um elemento
      // inerte não o aceita.
      else if (agora !== antes) raizes.get(agora!)!.inert = false;

      // Devolve o foco a quem abriu; sem isto ele volta para o topo da página. Quem abriu pode já
      // não existir (as duas camadas fecharam juntas, ou o botão que abriu sumiu com a escolha): aí o
      // foco fica com quem o recebeu antes.
      if (focoAnterior?.isConnected) focoAnterior.focus();
    };
  }, [tela]);

  return createPortal(
    <div
      ref={raiz}
      className={cx("fixed inset-0 z-50 flex", !tela && "items-center justify-center p-3 sm:p-6")}
    >
      {/* Na tela inteira não há fundo para tocar: o painel cobre tudo, e o Esc e o "Fechar" dele bastam. */}
      {!tela && (
        <button
          aria-label="Fechar"
          tabIndex={-1}
          onClick={() => {
            const id = idDaCamada.current;
            if (id !== null && ehDoTopo(pilha, id)) ultimoAoFechar.current();
          }}
          className="absolute inset-0 cursor-default bg-black/85"
        />
      )}

      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-label={rotulo}
        tabIndex={-1}
        className={cx(
          "relative flex flex-col overflow-hidden bg-fundo outline-none",
          tela
            ? // O `overscroll-none` impede o puxão do celular de arrastar a página de trás.
              "h-full w-full overscroll-none"
            : "max-h-full w-full max-w-3xl rounded-peca border border-borda shadow-2xl",
        )}
      >
        {/*
          A rolagem mora aqui, e não no painel: o painel recorta os cantos, e o
          conteúdo rola dentro dele. Sem esta camada, o que passava da altura da
          tela era simplesmente cortado — com um prompt longo, o botão de gerar
          ficava abaixo da borda, sem como alcançá-lo. `min-h-0` é o que deixa o
          item encolher abaixo do próprio conteúdo dentro do flex; sem ele, a
          rolagem nunca aparece.
        */}
        <div
          className={
            tela ? "flex min-h-0 flex-1 flex-col" : "min-h-0 overflow-y-auto overscroll-contain"
          }
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

const FOCAVEIS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Mantém o Tab (e o Shift+Tab) dentro do painel: do último elemento volta ao primeiro, e vice-versa. */
function prenderFoco(e: KeyboardEvent, painel: HTMLElement | null) {
  if (!painel) return;
  const focaveis = Array.from(painel.querySelectorAll<HTMLElement>(FOCAVEIS)).filter(
    (el) => el.getClientRects().length > 0,
  );
  const primeiro = focaveis[0];
  const ultimo = focaveis[focaveis.length - 1];
  if (!primeiro || !ultimo) {
    e.preventDefault();
    return;
  }
  const ativo = document.activeElement;
  // Foco que caiu para fora do painel (o botão em que estava foi desabilitado, por exemplo): volta.
  if (!ativo || !painel.contains(ativo)) {
    e.preventDefault();
    (e.shiftKey ? ultimo : primeiro).focus();
    return;
  }
  // O próprio painel (que recebe o foco ao abrir) conta como "antes do primeiro".
  if (e.shiftKey && (ativo === primeiro || ativo === painel)) {
    e.preventDefault();
    ultimo.focus();
  } else if (!e.shiftKey && ativo === ultimo) {
    e.preventDefault();
    primeiro.focus();
  }
}
